import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { apiRequest } from '../utils/apiHelper';
import { useBranding } from '../context/BrandingContext';
import '../styles/login.css';

const ActivateAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { branding, loading: brandingLoading } = useBranding();

  const logoSrc = branding?.logoUrl;
  const accent = branding?.colors?.accent || '#1e293b';
  const companyName = branding?.companyName || 'Sistem';

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const hasMinLength = formData.password.length >= 8;
  const hasUpper = /[A-Z]/.test(formData.password);
  const hasLower = /[a-z]/.test(formData.password);
  const hasNumber = /\d/.test(formData.password);
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasNumber;

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setFormError('Şifreler eşleşmiyor!');
      return;
    }

    if (!isPasswordValid) {
      setFormError('Lütfen tüm şifre kurallarını karşıladığınızdan emin olun.');
      return;
    }

    setLoading(true);

    try {
      await apiRequest('/auth/activate-account', {
        method: 'POST',
        data: {
          token,
          password: formData.password
        },
        skipAuth: true
      });

      setFormSuccess('Hesabınız başarıyla aktive edildi! Giriş yapabilirsiniz.');
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1500);
    } catch (error) {
      console.error('Account activation error:', error);
      setFormError(error.message || 'Hesap aktivasyonu başarısız. Link geçersiz veya daha önce kullanılmış olabilir.');
    } finally {
      setLoading(false);
    }
  };

  if (brandingLoading) {
    return (
      <div className="login-page-wrapper">
        <div className="login-container shadow-sm d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="login-page-wrapper">
        <div className="login-container shadow-sm">
          <div className="login-form-panel w-100" style={{ borderRight: 'none', borderRadius: '24px' }}>
            <div className="login-form-inner text-center py-5">
              <i className="fas fa-exclamation-circle fa-3x mb-3" style={{ color: '#ef4444' }}></i>
              <h4 className="login-form-title mb-2">Geçersiz Bağlantı</h4>
              <p className="login-form-desc mb-4">
                Hesap aktivasyon jetonu bulunamadı. Lütfen e-postanızdaki linke tıkladığınızdan emin olun.
              </p>
              <Link to="/login" className="login-btn mt-2" style={{ backgroundColor: accent, textDecoration: 'none', display: 'inline-block' }}>
                <i className="fas fa-arrow-left me-2"></i>
                Giriş Sayfasına Dön
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-wrapper">
      <div className="login-container shadow-sm" style={{ maxWidth: '500px', width: '90%' }}>
        <div className="login-form-panel w-100" style={{ borderRight: 'none', borderRadius: '24px' }}>
          
          <div className="login-mobile-header" style={{ '--accent': accent }}>
            {logoSrc ? (
              <img src={logoSrc} alt={companyName} className="login-mobile-logo" style={{ objectFit: 'contain' }} />
            ) : (
              <span className="login-mobile-name" style={{ color: accent }}>{companyName}</span>
            )}
          </div>

          <div className="login-form-inner">
            <div className="text-center mb-4 d-none d-md-block">
              {logoSrc ? (
                <img src={logoSrc} alt={companyName} style={{ height: '45px', objectFit: 'contain' }} className="mb-3" />
              ) : (
                <h4 style={{ color: accent, fontWeight: 'bold' }}>{companyName}</h4>
              )}
            </div>

            <h2 className="login-form-title text-center">Hesabınızı Aktifleştirin</h2>
            <p className="login-form-desc text-center">Güvenliğiniz için lütfen yeni bir şifre belirleyin.</p>

            {formError && (
              <div className="alert alert-danger d-flex align-items-center mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>
                <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '1.2rem' }} />
                <div>{formError}</div>
              </div>
            )}
            
            {formSuccess && (
              <div className="alert alert-success d-flex align-items-center mb-4" role="alert" style={{ fontSize: '0.875rem', borderRadius: '10px', backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                <i className="fas fa-check-circle me-2" style={{ fontSize: '1.2rem' }} />
                <div>{formSuccess}</div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="signup-form-grid" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              <div>
                <label className="login-label">Yeni Şifre</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-lock login-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="login-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    disabled={loading || formSuccess}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
                
                <div className="mt-2" style={{ fontSize: '0.75rem' }}>
                  <div className={`d-flex align-items-center ${hasMinLength ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasMinLength ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 8 karakter
                  </div>
                  <div className={`d-flex align-items-center ${hasUpper ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasUpper ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 1 büyük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasLower ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasLower ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 1 küçük harf
                  </div>
                  <div className={`d-flex align-items-center ${hasNumber ? 'text-success' : 'text-muted'}`}>
                    <i className={`fas ${hasNumber ? 'fa-check-circle' : 'fa-circle'} me-1`} style={{ fontSize: '0.6rem' }} /> En az 1 rakam
                  </div>
                </div>
              </div>

              <div>
                <label className="login-label">Şifre Tekrar</label>
                <div className="login-input-wrap" style={{ '--accent': accent }}>
                  <i className="fas fa-lock login-input-icon" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="login-input"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    disabled={loading || formSuccess}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fas fa-${showConfirmPassword ? 'eye-slash' : 'eye'}`} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="login-btn w-100 mt-2"
                style={{ backgroundColor: accent }}
                disabled={loading || formSuccess}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="fas fa-check-circle me-2"></i>
                )}
                Hesabı Aktifleştir
              </button>
            </form>

            <div className="text-center mt-4">
              <Link to="/login" className="login-forgot-link" style={{ color: '#64748b' }}>
                <i className="fas fa-arrow-left me-1"></i> Giriş Sayfasına Dön
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivateAccount;
